import type {
  ReaderDataProvider,
  BookData,
  ReadingPosition,
  Highlight,
  ChapterNote,
  Discussion,
  CreateHighlightParams,
  UpsertNoteParams,
} from '@fc/reader'
import * as api from './api'
import { mediaUrl } from './media'

export function createReaderAdapter(): ReaderDataProvider {
  return {
    async getBook(bookId: string): Promise<BookData | null> {
      try {
        const book = await api.books.getBySlug(bookId)
        if (!book) return null

        return {
          id: book.id,
          slug: book.slug,
          title: book.title,
          author: 'Fasting Carnivore',
          chapters: book.chapters.map((ch) => ({
            id: ch.id,
            number: ch.number,
            title: ch.title,
            content: ch.content,
            isCompleted: false,
            readingTime: estimateReadingTime(ch.content),
          })),
          materials: book.materials.map((m) => ({
            id: m.id,
            name: m.name,
            description: m.description,
            type: m.type,
            fileUrl: m.fileUrl,
            fileSize: m.fileSize,
          })),
        }
      } catch {
        return null
      }
    },

    async getReadingPosition(bookId: string): Promise<ReadingPosition | null> {
      try {
        return await api.reader.getPosition(bookId)
      } catch {
        return null
      }
    },

    async updateScrollPosition(bookId: string, chapter: number, position: number): Promise<void> {
      await api.reader.updatePosition(bookId, chapter, position)
    },

    async getChapterHighlights(chapterId: string): Promise<Highlight[]> {
      try {
        const highlights = await api.reader.getHighlights(chapterId)
        return highlights.map((h) => ({
          id: h.id,
          startOffset: h.startOffset,
          endOffset: h.endOffset,
          color: h.color,
          note: h.note,
          selectedText: h.selectedText,
          chapterNumber: h.chapterNumber,
        }))
      } catch {
        return []
      }
    },

    async createHighlight(params: CreateHighlightParams): Promise<{ highlightId: string }> {
      return await api.reader.createHighlight(params)
    },

    async updateHighlight(id: string, updates: { color?: string; note?: string }): Promise<void> {
      await api.reader.updateHighlight(id, updates)
    },

    async deleteHighlight(id: string): Promise<void> {
      await api.reader.deleteHighlight(id)
    },

    async getChapterNote(chapterId: string): Promise<ChapterNote | null> {
      try {
        const note = await api.reader.getNote(chapterId)
        if (!note) return null
        return { id: note.id, content: note.content, updatedAt: note.updatedAt }
      } catch {
        return null
      }
    },

    async upsertChapterNote(params: UpsertNoteParams): Promise<void> {
      await api.reader.upsertNote(params)
    },

    async deleteChapterNote(noteId: string): Promise<void> {
      await api.reader.deleteNote(noteId)
    },

    async getChapterDiscussions(chapterId: string): Promise<Discussion[]> {
      try {
        const discussions = await api.discussions.getChapter(chapterId)
        return discussions.map((d) => ({
          id: d.id,
          userName: d.userName,
          userAvatar: d.userAvatar,
          content: d.content,
          source: d.source,
          createdAt: d.createdAt,
        }))
      } catch {
        return []
      }
    },

    async postDiscussion(chapterId: string, content: string): Promise<void> {
      await api.discussions.post(chapterId, content)
    },

    async getChapterAudio(chapterId: string): Promise<{ audioUrl: string | null; hasAudio: boolean }> {
      try {
        const result = await api.audioApi.getChapterAudio(chapterId)
        if (result.hasAudio && result.audioUrl) {
          return { audioUrl: mediaUrl(result.audioUrl), hasAudio: true }
        }
        return { audioUrl: null, hasAudio: false }
      } catch {
        return { audioUrl: null, hasAudio: false }
      }
    },
  }
}

function estimateReadingTime(content: string): string {
  const words = content.split(/\s+/).length
  const minutes = Math.ceil(words / 250)
  return `${minutes} min`
}
